import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, RefreshCw } from "lucide-react";
import { settlementsApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP, fmtInt } from "../dashboard/format";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function ReportsScreen() {
  const [month, setMonth] = useState(currentMonth());

  const query = useQuery({
    queryKey: ["settlements", "monthly-report", month],
    queryFn: () => settlementsApi.monthlyReport(month),
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Liquidaciones · Reportes</p>
          <h1 className="nx-h1">Reporte mensual</h1>
          <p className="text-sm text-fg-muted mt-1">
            Vista agregada del mes: total transferido, cantidad de operaciones,
            días promedio entre aceptación y transferencia, y settlements que
            quedaron sin pagar al cierre del mes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-2xs text-fg-subtle uppercase tracking-wider font-semibold">
            <Calendar size={12} className="inline mr-1" />
            Mes
          </label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="nx-input w-auto"
          />
          <button
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="nx-btn-secondary"
          >
            <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando reporte…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : query.data ? (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Total transferido"
            value={fmtCLP(query.data.totalTransferido)}
            sub={`en ${query.data.cantidad} ${query.data.cantidad === 1 ? "operación" : "operaciones"}`}
            tone="success"
          />
          <KpiCard
            label="Cantidad"
            value={fmtInt(query.data.cantidad)}
            sub="transferencias realizadas"
          />
          <KpiCard
            label="Días promedio"
            value={query.data.diasAtrasoPromedio.toFixed(2)}
            sub="entre aceptación y transferencia"
            tone={
              query.data.diasAtrasoPromedio <= 3
                ? "success"
                : query.data.diasAtrasoPromedio <= 7
                  ? "warning"
                  : "danger"
            }
          />
          <KpiCard
            label="Pendientes al cierre"
            value={fmtInt(query.data.pendientesAlCierre)}
            sub="settlements aceptados sin transferir"
            tone={query.data.pendientesAlCierre > 0 ? "warning" : "success"}
          />
        </section>
      ) : null}

      <div className="nx-card p-4 bg-info-soft border-info/30">
        <div className="text-2xs text-fg-muted">
          <strong className="text-fg">Cómo se calcula:</strong>{" "}
          <span>
            Total y cantidad cuentan los <code>SettlementTransferRecord</code>{" "}
            con <code>createdAt</code> dentro del mes seleccionado. Los días
            promedio se calculan como <code>transferredAt − acceptedAt</code>{" "}
            por settlement. Pendientes al cierre = settlements en estados
            ACEPTADA/RESUELTA/TRANSFERENCIA_PENDIENTE con{" "}
            <code>acceptedAt</code> antes del fin de mes y sin TransferRecord
            en el mes.
          </span>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "success" | "warning" | "danger";
}) {
  const colorClass =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "danger"
          ? "text-danger"
          : "text-fg";
  return (
    <div className="nx-card p-4">
      <div className="nx-eyebrow mb-2">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${colorClass}`}>{value}</div>
      <div className="text-2xs text-fg-subtle mt-1">{sub}</div>
    </div>
  );
}
